import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  QrCodeIcon,
  CreditCardIcon,
  UserIcon,
  CalendarDaysIcon,
  MapPinIcon,
  CakeIcon,
  ArrowDownTrayIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  PlusIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import {
  getMealSession,
  getMealSessions,
  getSessionAttendance,
  recordAttendance,
  bulkRecordAttendance,
  type MealSession,
  type MealAttendance,
  type MealType,
} from '@/api/mealsApi'
import { getUsers, type User } from '@/api/usersApi'
import { getRFIDCards } from '@/api/rfidApi'
import toast from 'react-hot-toast'

const statusColors: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
  present: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon },
  absent: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon },
  excused: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon },
}

const portionOptions = [
  { value: 'full', label: 'Full', color: 'bg-green-100 text-green-800' },
  { value: 'half', label: 'Half', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'none', label: 'None', color: 'bg-gray-100 text-gray-800' },
]

const MealAttendancePage: React.FC = () => {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const sessionIdParam = searchParams.get('sessionId')
  const rfidInputRef = useRef<HTMLInputElement>(null)

  const [sessions, setSessions] = useState<MealSession[]>([])
  const [selectedSession, setSelectedSession] = useState<MealSession | null>(null)
  const [attendance, setAttendance] = useState<MealAttendance[]>([])
  const [stats, setStats] = useState({ total: 0, present: 0, absent: 0, excused: 0 })
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [portionFilter, setPortionFilter] = useState<string>('all')
  const [rfidInput, setRfidInput] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Student search modal
  const [showStudentSearch, setShowStudentSearch] = useState(false)
  const [studentSearchQuery, setStudentSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [searchingStudents, setSearchingStudents] = useState(false)

  // Portion edit modal
  const [showPortionModal, setShowPortionModal] = useState(false)
  const [editingRecord, setEditingRecord] = useState<MealAttendance | null>(null)
  const [selectedPortion, setSelectedPortion] = useState<'full' | 'half' | 'none'>('full')

  // Recent check-ins for visual feedback
  const [recentCheckIns, setRecentCheckIns] = useState<{ name: string; time: string; id: string }[]>([])

  useEffect(() => {
    if (sessionIdParam) {
      fetchSessionDetails(sessionIdParam)
    } else {
      fetchActiveSessions()
    }
  }, [sessionIdParam])

  // Auto-focus RFID input when session is in progress
  useEffect(() => {
    if (selectedSession?.status === 'in_progress' && rfidInputRef.current) {
      rfidInputRef.current.focus()
    }
  }, [selectedSession])

  const fetchActiveSessions = async () => {
    try {
      setLoading(true)
      const response = await getMealSessions({
        status: 'in_progress',
        limit: 50,
      })
      setSessions(response.data.sessions)

      if (response.data.sessions.length === 1) {
        setSelectedSession(response.data.sessions[0])
        fetchAttendance(response.data.sessions[0]._id)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }

  const fetchSessionDetails = async (sessionId: string) => {
    try {
      setLoading(true)
      const response = await getMealSession(sessionId)
      setSelectedSession(response.data)
      setAttendance(response.data.attendance || [])
      calculateStats(response.data.attendance || [])
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load session')
      navigate('/schoolAdmin/meals/sessions')
    } finally {
      setLoading(false)
    }
  }

  const fetchAttendance = async (sessionId: string) => {
    try {
      const response = await getSessionAttendance(sessionId)
      setAttendance(response.data.attendance)
      setStats(response.data.stats)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load attendance')
    }
  }

  const calculateStats = (attendanceList: MealAttendance[]) => {
    const newStats = {
      total: attendanceList.length,
      present: attendanceList.filter(a => a.status === 'present').length,
      absent: attendanceList.filter(a => a.status === 'absent').length,
      excused: attendanceList.filter(a => a.status === 'excused').length,
    }
    setStats(newStats)
  }

  const handleRecordAttendance = async (
    studentId: string,
    status: 'present' | 'absent' | 'excused',
    portion: 'full' | 'half' | 'none' = 'full',
    checkInMethod: 'manual' | 'rfid' | 'qr_code' = 'manual',
    studentName?: string
  ) => {
    if (!selectedSession) return
    try {
      await recordAttendance(selectedSession._id, {
        studentId,
        status,
        checkInMethod,
        portion,
      })

      // Add to recent check-ins
      if (status === 'present' && studentName) {
        setRecentCheckIns(prev => [
          { name: studentName, time: new Date().toLocaleTimeString(), id: studentId },
          ...prev.slice(0, 4)
        ])
      }

      toast.success(`Marked as ${status}`)
      fetchAttendance(selectedSession._id)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to record attendance')
    }
  }

  const handleRfidScan = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!rfidInput.trim() || !selectedSession) return

    try {
      setSubmitting(true)

      // Look up the RFID card
      const rfidResponse = await getRFIDCards({ search: rfidInput.trim() })
      const cards = rfidResponse.data || []

      // Find exact match
      const card = cards.find(c =>
        c.cardNumber === rfidInput.trim() ||
        c.rfidTag === rfidInput.trim()
      )

      if (!card) {
        toast.error('RFID card not found')
        setRfidInput('')
        rfidInputRef.current?.focus()
        return
      }

      if (!card.assignedToId) {
        toast.error('This card is not assigned to any student')
        setRfidInput('')
        rfidInputRef.current?.focus()
        return
      }

      // Record attendance
      await handleRecordAttendance(
        card.assignedToId,
        'present',
        'full',
        'rfid',
        card.assignedToName || 'Student'
      )

      setRfidInput('')
      rfidInputRef.current?.focus()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to process RFID')
    } finally {
      setSubmitting(false)
    }
  }

  const handleStudentSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([])
      return
    }

    try {
      setSearchingStudents(true)
      const response = await getUsers({ search: query, limit: 10 })
      // Filter for students only
      const students = response.users.filter(u =>
        u.role === 'Student' || u.role === 'STUDENT'
      )
      setSearchResults(students)
    } catch (error) {
      console.error('Failed to search students:', error)
    } finally {
      setSearchingStudents(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (studentSearchQuery) {
        handleStudentSearch(studentSearchQuery)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [studentSearchQuery, handleStudentSearch])

  const handleAddStudent = async (student: User) => {
    await handleRecordAttendance(
      student.id,
      'present',
      'full',
      'manual',
      `${student.firstName} ${student.lastName}`
    )
    setShowStudentSearch(false)
    setStudentSearchQuery('')
    setSearchResults([])
  }

  const handleEditPortion = async () => {
    if (!editingRecord || !selectedSession) return

    try {
      setSubmitting(true)
      const studentId = typeof editingRecord.studentId === 'object'
        ? editingRecord.studentId._id
        : editingRecord.studentId

      await recordAttendance(selectedSession._id, {
        studentId,
        status: editingRecord.status,
        portion: selectedPortion,
      })

      toast.success('Portion updated')
      setShowPortionModal(false)
      setEditingRecord(null)
      fetchAttendance(selectedSession._id)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update portion')
    } finally {
      setSubmitting(false)
    }
  }

  const exportAttendance = () => {
    if (!selectedSession || attendance.length === 0) return

    const mealTypeName = getMealTypeName(selectedSession)
    const date = formatDate(selectedSession.sessionDate)

    const csvContent = [
      ['Student Name', 'Student ID', 'Status', 'Check-in Time', 'Method', 'Portion', 'Notes'].join(','),
      ...attendance.map(record => [
        `"${getStudentName(record.studentId)}"`,
        `"${getStudentId(record.studentId)}"`,
        record.status,
        record.checkInTime ? new Date(record.checkInTime).toLocaleTimeString() : '-',
        record.checkInMethod,
        record.portion,
        `"${record.notes || ''}"`,
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `meal-attendance-${mealTypeName}-${date}.csv`.toLowerCase().replace(/\s+/g, '-')
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)

    toast.success('Attendance exported')
  }

  const selectSession = (session: MealSession) => {
    setSelectedSession(session)
    fetchAttendance(session._id)
    window.history.replaceState(null, '', `?sessionId=${session._id}`)
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const h = parseInt(hours)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const displayHour = h % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  const getMealTypeName = (session: MealSession) => {
    if (typeof session.mealTypeId === 'object' && session.mealTypeId?.name) {
      return session.mealTypeId.name
    }
    return 'Meal'
  }

  const getStudentName = (student: MealAttendance['studentId']) => {
    if (typeof student === 'object' && student) {
      return `${student.firstName} ${student.lastName}`
    }
    return 'Unknown Student'
  }

  const getStudentId = (student: MealAttendance['studentId']): string => {
    if (typeof student === 'object' && student) {
      return student.studentId || student._id
    }
    return student as string
  }

  const filteredAttendance = attendance.filter(a => {
    const studentName = getStudentName(a.studentId).toLowerCase()
    const matchesSearch = studentName.includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter
    const matchesPortion = portionFilter === 'all' || a.portion === portionFilter
    return matchesSearch && matchesStatus && matchesPortion
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Session selection view
  if (!selectedSession && !sessionIdParam) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meal Attendance</h1>
          <p className="text-gray-600 mt-1">
            Select an active session to record attendance
          </p>
        </div>

        {sessions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Sessions</h3>
            <p className="text-gray-600 mb-4">
              There are no meal sessions currently in progress.
            </p>
            <Button onClick={() => navigate('/schoolAdmin/meals/sessions')}>
              View All Sessions
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sessions.map((session) => (
              <motion.div
                key={session._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => selectSession(session)}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CakeIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {getMealTypeName(session)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {formatDate(session.sessionDate)}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-4 w-4" />
                    <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <UserGroupIcon className="h-4 w-4" />
                    <span>{session.actualCount} / {session.expectedCount} recorded</span>
                  </div>
                </div>

                <Button className="w-full mt-4">
                  Record Attendance
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Attendance recording view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/schoolAdmin/meals/sessions')}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {selectedSession && getMealTypeName(selectedSession)} Attendance
          </h1>
          <p className="text-gray-600">
            {selectedSession && formatDate(selectedSession.sessionDate)} &bull;{' '}
            {selectedSession && formatTime(selectedSession.startTime)} - {selectedSession && formatTime(selectedSession.endTime)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportAttendance}
            disabled={attendance.length === 0}
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.present}</p>
              <p className="text-sm text-gray-500">Present</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircleIcon className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
              <p className="text-sm text-gray-500">Absent</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{stats.excused}</p>
              <p className="text-sm text-gray-500">Excused</p>
            </div>
          </div>
        </div>
      </div>

      {/* RFID Scanner & Quick Add */}
      {selectedSession?.status === 'in_progress' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* RFID Scanner */}
          <div className="lg:col-span-2 bg-gradient-to-r from-primary-600 to-indigo-600 rounded-xl shadow-sm p-6 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <CreditCardIcon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold">Quick Check-in</h3>
                <p className="text-sm text-white/80">Scan RFID card or enter card number</p>
              </div>
            </div>
            <form onSubmit={handleRfidScan} className="flex gap-3">
              <input
                ref={rfidInputRef}
                type="text"
                value={rfidInput}
                onChange={(e) => setRfidInput(e.target.value)}
                placeholder="Scan RFID or enter card number..."
                className="flex-1 px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                autoFocus
              />
              <Button
                type="submit"
                disabled={submitting || !rfidInput.trim()}
                className="bg-white text-primary-600 hover:bg-white/90"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                ) : (
                  <>
                    <QrCodeIcon className="h-5 w-5 mr-2" />
                    Check In
                  </>
                )}
              </Button>
            </form>

            {/* Recent check-ins */}
            {recentCheckIns.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-sm text-white/80 mb-2">Recent check-ins:</p>
                <div className="flex flex-wrap gap-2">
                  {recentCheckIns.map((checkIn, idx) => (
                    <span
                      key={`${checkIn.id}-${idx}`}
                      className="px-2 py-1 bg-white/20 rounded text-sm"
                    >
                      {checkIn.name} ({checkIn.time})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Manual Add */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <PlusIcon className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Manual Entry</h3>
                <p className="text-sm text-gray-500">Search and add student</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowStudentSearch(true)}
            >
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
              Search Students
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="present">Present</option>
            <option value="absent">Absent</option>
            <option value="excused">Excused</option>
          </select>

          <select
            value={portionFilter}
            onChange={(e) => setPortionFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Portions</option>
            <option value="full">Full</option>
            <option value="half">Half</option>
            <option value="none">None</option>
          </select>
        </div>
      </div>

      {/* Attendance List */}
      {filteredAttendance.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No attendance records</h3>
          <p className="text-gray-600">
            {searchQuery || statusFilter !== 'all' || portionFilter !== 'all'
              ? 'No records match your filters'
              : 'Start recording attendance using RFID or manual entry'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check-in Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Portion
                  </th>
                  {selectedSession?.status === 'in_progress' && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAttendance.map((record) => {
                  const StatusIcon = statusColors[record.status]?.icon || CheckCircleIcon
                  return (
                    <tr key={record._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {getStudentName(record.studentId)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {getStudentId(record.studentId)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {record.checkInTime
                          ? new Date(record.checkInTime).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            })
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                          {record.checkInMethod === 'rfid' && <CreditCardIcon className="h-4 w-4" />}
                          {record.checkInMethod === 'qr_code' && <QrCodeIcon className="h-4 w-4" />}
                          {record.checkInMethod === 'manual' && <UserIcon className="h-4 w-4" />}
                          {record.checkInMethod}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[record.status]?.bg} ${statusColors[record.status]?.text}`}>
                          <StatusIcon className="h-3.5 w-3.5" />
                          {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setEditingRecord(record)
                            setSelectedPortion(record.portion)
                            setShowPortionModal(true)
                          }}
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            portionOptions.find(p => p.value === record.portion)?.color || 'bg-gray-100 text-gray-800'
                          } hover:opacity-80 transition-opacity`}
                          disabled={selectedSession?.status !== 'in_progress'}
                        >
                          {record.portion}
                        </button>
                      </td>
                      {selectedSession?.status === 'in_progress' && (
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            {record.status !== 'present' && (
                              <button
                                onClick={() => {
                                  const studentId = typeof record.studentId === 'object' ? record.studentId._id : record.studentId
                                  handleRecordAttendance(studentId, 'present')
                                }}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                                title="Mark Present"
                              >
                                <CheckCircleIcon className="h-5 w-5" />
                              </button>
                            )}
                            {record.status !== 'absent' && (
                              <button
                                onClick={() => {
                                  const studentId = typeof record.studentId === 'object' ? record.studentId._id : record.studentId
                                  handleRecordAttendance(studentId, 'absent')
                                }}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Mark Absent"
                              >
                                <XCircleIcon className="h-5 w-5" />
                              </button>
                            )}
                            {record.status !== 'excused' && (
                              <button
                                onClick={() => {
                                  const studentId = typeof record.studentId === 'object' ? record.studentId._id : record.studentId
                                  handleRecordAttendance(studentId, 'excused')
                                }}
                                className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                                title="Mark Excused"
                              >
                                <ClockIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Student Search Modal */}
      <AnimatePresence>
        {showStudentSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowStudentSearch(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Search Students</h2>
                  <button
                    onClick={() => setShowStudentSearch(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by name or ID..."
                    value={studentSearchQuery}
                    onChange={(e) => setStudentSearchQuery(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {searchingStudents ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    {studentSearchQuery.length < 2
                      ? 'Type at least 2 characters to search'
                      : 'No students found'}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {searchResults.map((student) => (
                      <button
                        key={student.id}
                        onClick={() => handleAddStudent(student)}
                        className="w-full p-4 text-left hover:bg-gray-50 flex items-center gap-3"
                      >
                        <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {student.firstName} {student.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {student.studentId || student.email}
                          </p>
                        </div>
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Portion Edit Modal */}
      <AnimatePresence>
        {showPortionModal && editingRecord && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPortionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Edit Portion for {getStudentName(editingRecord.studentId)}
              </h2>

              <div className="space-y-2 mb-6">
                {portionOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedPortion(option.value as 'full' | 'half' | 'none')}
                    className={`w-full p-3 rounded-lg border-2 flex items-center justify-between transition-colors ${
                      selectedPortion === option.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="font-medium">{option.label}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${option.color}`}>
                      {option.value}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowPortionModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEditPortion}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MealAttendancePage
